import axios from "../../../api/axios";
import Axios from "axios";
import { stripIncompleteDrawingPin } from "./documentUploadDrawingUtils";

/**
 * Normalize ProjectFolderSelector onChange into `{ id, displayTitle, title }`.
 * The selector emits `{ target: { value }, object }` — callers that only keep `id`
 * lose the trigger label (`displayTitle`) and show «ikke valgt».
 */
export function folderFromSelectorEvent(event) {
  const folderId = event?.target?.value ?? event?.object?.id ?? null;
  if (folderId == null || folderId === "") return null;
  const obj = event.object || {};
  return {
    id: folderId,
    displayTitle: obj.displayTitle || obj.title || null,
    title: obj.title || obj.displayTitle || null,
  };
}

/**
 * When the folder selector is shown on a project/folder upload, «none» means
 * project root. Other modules (checklist, deviation, …) keep their original target.
 */
export function folderUploadFallback(module, moduleParam, folderProjectId) {
  if (folderProjectId && (module === "project" || module === "folder")) {
    return { module: "project", moduleParam: folderProjectId };
  }
  return { module, moduleParam };
}

/**
 * Bind a staged upload entry to a project folder.
 * Uploads API (`presigned_url` / `complete_upload`) uses `module` + `module_param`,
 * not `folder_id` — folder files must be `module=folder`, `moduleParam=<folder id>`.
 */
export function bindUploadEntryToFolder(entry, folder, fallbackModule, fallbackParam) {
  if (folder?.id) {
    return {
      ...entry,
      folder_id: folder.id,
      module: "folder",
      moduleParam: folder.id,
    };
  }
  return {
    ...entry,
    folder_id: null,
    module: fallbackModule,
    moduleParam: fallbackParam,
  };
}

async function uploadSingleDocument(entry, callbacks = {}) {
  const safeEntry = stripIncompleteDrawingPin(entry) || entry;
  const {
    file,
    title,
    module,
    moduleParam,
    folder_id,
    drawing_id,
    drawing_position_x,
    drawing_position_y,
    drawing_rotation,
    drawing_page,
    drawing_version_id,
    drawing_snapshot_document_id,
    skip_drawing_pin,
  } = safeEntry;
  const { onProgressChange, onFileUploadComplete } = callbacks;

  const checkpoint = entry.uploadCheckpoint;
  const renewPhoto = module === "status_update" && checkpoint?.prepared && !checkpoint.transferred;
  const response = (!renewPhoto && checkpoint?.prepared) || await axios.get("/uploads/presigned_url", {
    params: {
      ...(renewPhoto ? { id: checkpoint.prepared.data.document_id } : {}),
      filename: file.name,
      module,
      module_param: moduleParam,
      title,
      folder_id: folder_id || null,
    },
  });

  const { url, document_id } = response.data;
  if (!url || !document_id) {
    throw new Error("Invalid presigned URL response");
  }
  if (checkpoint) checkpoint.prepared = response;

  const isDevUpload = url.includes("/uploads/dev_upload/");
  const uploadOptions = {
    headers: { "Content-Type": file.type || "application/octet-stream" },
    onUploadProgress: (progressEvent) => {
      if (progressEvent.total && onProgressChange) {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgressChange({ [document_id]: percentCompleted });
      }
    },
  };

  if (checkpoint?.transferred) {
    // Retrying completion reuses the same document and uploaded bytes.
  } else if (isDevUpload) {
    await axios.put(`/uploads/dev_upload/${document_id}`, file, uploadOptions);
  } else {
    await Axios.put(url, file, uploadOptions);
  }
  if (checkpoint) checkpoint.transferred = true;

  const completePayload = {
    id: document_id,
    filename: file.name,
    title,
    module,
    module_param: moduleParam,
    folder_id: folder_id ?? null,
    drawing_id: drawing_id ?? null,
    drawing_position_x: drawing_position_x ?? null,
    drawing_position_y: drawing_position_y ?? null,
    drawing_rotation: drawing_rotation ?? 0,
  };
  if (drawing_page != null && drawing_page !== "") {
    completePayload.drawing_page = Number(drawing_page);
  }
  if (drawing_version_id != null && drawing_version_id !== "") {
    completePayload.drawing_version_id = drawing_version_id;
  }
  if (drawing_snapshot_document_id) {
    completePayload.drawing_snapshot_document_id = drawing_snapshot_document_id;
    completePayload.include_drawing_snapshot_in_print = true;
  }
  if (skip_drawing_pin) {
    completePayload.skip_drawing_pin = true;
  }

  const fileResponse = await axios.patch("/uploads/complete_upload", completePayload);

  if (onFileUploadComplete) {
    onFileUploadComplete();
  }

  return fileResponse.data?.document || null;
}

/**
 * Upload staged document files (same flow as UploaderV4).
 * @param {Array} filesToUpload - entries with { file, title, module, moduleParam, folder_id, drawing_id, ... }
 * @param {{ onProgressChange?: (p: Record<string, number>) => void, onFileUploadComplete?: () => void }} callbacks
 * @returns {Promise<Array>} uploaded document records (falsy responses omitted)
 */
export async function uploadDocumentFiles(filesToUpload, callbacks = {}) {
  const uploadedFiles = [];

  for (const entry of filesToUpload) {
    if (!entry?.file) continue;
    if (entry.uploadCheckpoint?.document) {
      uploadedFiles.push(entry.uploadCheckpoint.document);
      callbacks.onFileUploadComplete?.();
      continue;
    }

    const {
      title,
      module,
      moduleParam,
      folder_id,
      drawing_snapshot_blob,
    } = entry;

    let snapshotDocumentId = null;
    // Snapshot only when the main photo will get a pin (drawing_id + coords after strip).
    const pinReadyEntry = stripIncompleteDrawingPin(entry);
    const willPin = Boolean(pinReadyEntry?.drawing_id);
    if (drawing_snapshot_blob && willPin) {
      try {
        const snapshotFile = new File(
          [drawing_snapshot_blob],
          `drawing-snapshot-${Date.now()}.jpg`,
          { type: "image/jpeg" }
        );
        // Snapshot helper must NOT send drawing_id/coords: complete_upload requires
        // pin coordinates for checklist_item/deviation when drawing_id is set, and
        // DeviationDocumentPin/ChecklistItemDocumentPin would also create a duplicate pin.
        // The main document links the snapshot via drawing_snapshot_document_id.
        const snapshotDoc = await uploadSingleDocument(
          {
            file: snapshotFile,
            title: `${title || "Photo"} – drawing snapshot`,
            module,
            moduleParam,
            folder_id,
            skip_drawing_pin: true,
          },
          {}
        );
        snapshotDocumentId = snapshotDoc?.id || null;
      } catch (snapshotErr) {
        console.error("Failed to upload drawing snapshot:", snapshotErr);
        // If a snapshot was intended but failed to upload, fail the entire upload for this entry
        // to avoid silently completing without linking the expected drawing snapshot.
        throw snapshotErr;
      }
    }

    const documentRecord = await uploadSingleDocument(
      {
        ...pinReadyEntry,
        drawing_snapshot_document_id: snapshotDocumentId,
      },
      callbacks
    );

    if (documentRecord?.id != null) {
      if (entry.uploadCheckpoint) entry.uploadCheckpoint.document = documentRecord;
      uploadedFiles.push(documentRecord);
    } else {
      throw new Error("Upload completed without a document confirmation");
    }
  }

  return uploadedFiles;
}
